import { useEffect, useRef, useState } from 'react';
import Modal from '@/components/Modal';
import { TFunction } from 'i18next';
import { getFileToUpload, mapComponentOptions } from '@/utils/ia.utils';
import { Select } from '@/components/Controls/Select';
import { twMerge } from 'tailwind-merge';
import { ModerationReportCanvas } from '@/components/Custom/ModerationReportCanvas';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import { MODE_DRAW, NOTIFY, R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { useGetAllComponentKey, useGetAllDefectKey } from '@/hooks/ai.hooks';
import Loader from '@/components/Loading/Loader';
import {
  useCreateManyFirestoreImages,
  useCreateManyProcessings,
  useUploadProcessingImage,
} from '@/hooks/ai.hooks';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { useAuth } from '@/hooks/auth.hooks';
import { notify } from '@/utils/toast.utils';
import { getDamageById, getDamageByClassId } from '@/api/damage/damage';
import {
  getEquipmentById,
  getEquipmentByClassId,
} from '@/api/equipment/equipment';
import { UUID_REGEX } from '@/utils/regex.utils';
import { usePostFileSFTP } from '@/hooks/sftp.hooks';
import { FileData } from '@/api/entities/engineCorrection';
import { useDenormalizePolygonPoints } from '@/hooks/moderation.hooks';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';

const MODAL_ID = 'add-inspection-photo-to-engine-correction-modal';

interface ItemCoordinates {
  elNumber: string;
  point: object[];
}

interface AddInspectionPhotoToEngineCorrectionModalProps {
  t?: TFunction<undefined>;
  item: TResultObjectSchema;
  openModalCount?: number;
  onAddedToEngineCorrection?: () => void;
}

export const AddInspectionPhotoToEngineCorrectionModal = ({
  t,
  item,
  openModalCount,
  onAddedToEngineCorrection,
}: AddInspectionPhotoToEngineCorrectionModalProps) => {
  const { loggedInUser } = useAuth();

  const [selectedComponentKey, setSelectedComponentKey] = useState<string>();
  const [selectedDefectKey, setSelectedDefectKey] = useState<string>();
  const [itemCoordinates, setItemCoordinates] =
    useState<ItemCoordinates>(undefined);
  const [isLoadingItemCoordinates, setIsLoadingItemCoordinates] =
    useState(false);
  const [isPreparingUpload, setIsPreparingUpload] = useState(false);
  const uploadedImageFileRef = useRef<File | null>(null);
  const imageDimensionsRef = useRef<{ width: number; height: number } | null>(
    null,
  );

  const isDamage = !!item?.damage;

  const { data: keyCars, isLoading: isLoadingKeyCars } =
    useGetAllComponentKey();
  const { data: defectKeyCars, isLoading: isLoadingDefectKeyCars } =
    useGetAllDefectKey();

  const { mutate: uploadFileToSFTP, isPending: isPendingUploadFileToSFTP } =
    usePostFileSFTP({
      handleSuccess: () => {
        notify(
          t('common:photoAddedToEngineCorrectionSuccessfully'),
          NOTIFY.SUCCESS,
        );
        onAddedToEngineCorrection?.();
      },
    });

  const {
    mutate: createManyProcessings,
    isPending: isPendingCreateManyProcessings,
  } = useCreateManyProcessings({
    t,
    showDefaultSuccessMessage: false,
  });

  const {
    mutate: createManyFirestoreImages,
    isPending: isPendingCreateManyFirestoreImages,
  } = useCreateManyFirestoreImages({ t });

  useEffect(() => {
    if (isDamage && item?.damage?.id) {
      (async () => {
        try {
          let response = undefined;

          setIsLoadingItemCoordinates(true);

          const damageId = item?.damage?.id;

          if (UUID_REGEX.test(damageId)) {
            response = await getDamageById(damageId);
          } else {
            response = await getDamageByClassId(Number(damageId));
          }

          if (response?.status === 200 && response?.statusText === 'OK') {
            setItemCoordinates({
              elNumber: String(response?.data?.classId),
              point: item?.coordinatesModerated?.point,
            });
          }
        } catch (error) {
          console.error(error);
          setItemCoordinates(undefined);
        } finally {
          setIsLoadingItemCoordinates(false);
        }
      })();
    } else if (!isDamage && item?.equipment?.id) {
      (async () => {
        try {
          let response = undefined;

          setIsLoadingItemCoordinates(true);

          const equipmentId = item?.equipment?.id;

          if (UUID_REGEX.test(equipmentId)) {
            response = await getEquipmentById(equipmentId);
          } else {
            response = await getEquipmentByClassId(Number(equipmentId));
          }

          if (response?.status === 200 && response?.statusText === 'OK') {
            setItemCoordinates({
              elNumber: String(response?.data?.classId),
              point: item?.coordinatesModerated?.point,
            });
          }
        } catch (error) {
          console.error(error);
          setItemCoordinates(undefined);
        } finally {
          setIsLoadingItemCoordinates(false);
        }
      })();
    }
  }, [openModalCount, isDamage, item]);

  const { getDenormalizedCoordinates } = useDenormalizePolygonPoints({
    originalBoundingBox: item?.originalBoundingBox,
    coordinates: itemCoordinates as TResultImageCoordinatesSchema,
    imageDimension: imageDimensionsRef.current,
  });

  const { mutate: uploadImage, isPending: isPendingUploadImage } =
    useUploadProcessingImage({
      handleSuccess: (data: {
        data: { files: Array<{ filename: string; path: string }> };
      }) => {
        const file = data.data.files[0];
        const filePath = `${R2_PUBLIC_DOMAIN}/${file.path}`;

        createManyProcessings([
          {
            status: HumanModerationStatus.MODERE,
            publicUrl: filePath,
            fileName: filePath,
            numElementsDetected: 0,
            numDamagesDetected: 0,
            key: selectedComponentKey,
            defectKey: selectedDefectKey,
            isDamage: !!selectedDefectKey,
            isManualUpload: false,
            moderatedClassesElements: isDamage
              ? undefined
              : [item?.equipment?.name],
            moderationsElements: isDamage
              ? undefined
              : [getDenormalizedCoordinates()],
            moderatedClassesDamages: isDamage
              ? [item?.damage?.name]
              : undefined,
            moderationsDamages: isDamage
              ? [getDenormalizedCoordinates()]
              : undefined,
            moderatorUserId: loggedInUser?.id,
            moderationVerifiedByUserId: loggedInUser?.id,
          },
        ]);

        createManyFirestoreImages([
          {
            imageUrl: filePath,
            key: selectedComponentKey,
            defectKey: selectedDefectKey,
            isDamage: !!selectedDefectKey,
          },
        ]);

        const fileData: FileData = {
          elementAnnotation: null,
          dmgAnnotation: null,
          image: uploadedImageFileRef.current,
        };

        const denormalized = getDenormalizedCoordinates();
        if (denormalized) {
          const hasPolygonPoints = denormalized?.point?.length > 0;
          const annotationFile = getFileToUpload(
            [denormalized],
            { fileName: filePath },
            hasPolygonPoints,
          );

          if (isDamage) {
            fileData.dmgAnnotation = annotationFile;
          } else {
            fileData.elementAnnotation = annotationFile;
          }
        }

        uploadFileToSFTP({ fileData });
      },
    });

  useEffect(() => {
    setSelectedComponentKey(undefined);
    setSelectedDefectKey(undefined);
  }, [openModalCount]);

  const handleConfirm = async () => {
    if (!item?.image) return;

    setIsPreparingUpload(true);

    const imgParamToUpload = item?.image.includes('&token=')
      ? `&uuid=${crypto.randomUUID()}`
      : `?uuid=${crypto.randomUUID()}`;

    const blob = await new Promise<Blob>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageDimensionsRef.current = {
          width: img.width,
          height: img.height,
        };
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d')?.drawImage(img, 0, 0);
        canvas.toBlob((b) => resolve(b || new Blob()), 'image/jpeg', 0.9);
      };
      img.src = `${item?.image}${imgParamToUpload}`;
    });

    setIsPreparingUpload(false);

    const file = new File([blob], `${crypto.randomUUID()}.jpg`, {
      type: 'image/jpeg',
    });
    uploadedImageFileRef.current = file;
    uploadImage([file]);
  };

  return (
    <>
      <Modal
        id={MODAL_ID}
        title={t('common:addToEngineCorrection_2')}
        size="large"
        containerClassName="top-6"
        bodyClassName="px-6 pt-5 m-0"
        showSecondBtn
        secondBtnLabel={t('common:confirm')}
        disabledSubmitButton={false}
        onClickSecondBtn={handleConfirm}
      >
        <div
          className={twMerge(
            'mb-5 border border-gray-E5E5E5 px-5 pt-4 pb-6 rounded-lg',
          )}
        >
          <div className="font-medium text-primary mb-7">
            <span>{t('common:chooseEquipmentAndOrDamage')}</span>
            <span className="ml-1.5 font-semibold">:</span>
          </div>
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-700 font-medium">{`${t(
                'common:chooseAnEquipment',
              )} :`}</div>
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(keyCars?.data || [], '1', t)}
                onChange={(e) => setSelectedComponentKey(e.target.value)}
                value={selectedComponentKey}
              />
            </div>
            <div className="text-gray-300 ml-16 mr-12">|</div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-700 font-medium">{`${t(
                'common:chooseADamage',
              )} :`}</div>
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(defectKeyCars?.data || [], '2', t)}
                onChange={(e) => setSelectedDefectKey(e.target.value)}
                value={selectedDefectKey}
              />
            </div>
          </div>
        </div>
        {item?.image && (
          <div className="relative flex justify-center items-center after:content-[''] after:block after:w-full after:h-full after:absolute after:top-0 after:left-0 after:bg-transparent">
            <ModerationReportCanvas
              t={t}
              imageClassName="max-h-[58vh] object-contain object-center"
              imgSrc={item?.image}
              initialCoordinates={item?.coordinatesModerated}
              originalBoundingBox={item?.originalBoundingBox}
              mode={
                item?.coordinatesModerated?.point?.length > 0
                  ? MODE_DRAW.POLYGON
                  : MODE_DRAW.RECTANGLE
              }
            />
          </div>
        )}
      </Modal>
      {(isLoadingKeyCars ||
        isLoadingDefectKeyCars ||
        isPreparingUpload ||
        isPendingUploadImage ||
        isLoadingItemCoordinates ||
        isPendingCreateManyProcessings ||
        isPendingCreateManyFirestoreImages ||
        isPendingUploadFileToSFTP) && <Loader />}
    </>
  );
};
